/**
 * RNEvaluationDialog - RN/Supervising Nurse Evaluation dialog
 * Extracted from HRPayrollPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Stethoscope } from "lucide-react";

function getGradeColor(grade) {
  switch (grade) {
    case 1: return "bg-emerald-100 text-emerald-800";
    case 2: return "bg-green-100 text-green-800";
    case 3: return "bg-yellow-100 text-yellow-800";
    case 4: return "bg-orange-100 text-orange-800";
    case 5: return "bg-red-100 text-red-800";
    default: return "bg-slate-100 text-slate-600";
  }
}

export function RNEvaluationDialog({
  open,
  onOpenChange,
  employees,
  rnDuties,
  rnGradingScale,
  newRnEvaluation,
  setNewRnEvaluation,
  saving,
  onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-blue-600" />
            New RN/Supervising Nurse Evaluation
          </DialogTitle>
          <DialogDescription>
            Evaluate supervising nurse performance across agency management responsibilities
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Employee Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Select RN Employee *</Label>
              <Select
                value={newRnEvaluation.employee_id}
                onValueChange={(v) => {
                  const emp = employees.find(e => e.id === v);
                  setNewRnEvaluation(prev => ({ 
                    ...prev, 
                    employee_id: v,
                    employee_name: emp?.name || ""
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select RN to evaluate" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.role === "rn").map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Evaluation Date</Label>
              <Input
                type="date"
                value={newRnEvaluation.evaluation_date}
                onChange={(e) => setNewRnEvaluation(prev => ({ ...prev, evaluation_date: e.target.value }))}
              />
            </div>
          </div>

          {/* Position Info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm text-slate-500">Title of Position</p>
              <p className="font-medium">SUPERVISING NURSE</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Title of Immediate Supervisor</p>
              <p className="font-medium">ADMINISTRATOR</p>
            </div>
          </div>

          {/* Evaluation Period */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Period Start</Label>
              <Input
                type="date"
                value={newRnEvaluation.period_start}
                onChange={(e) => setNewRnEvaluation(prev => ({ ...prev, period_start: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Period End</Label>
              <Input
                type="date"
                value={newRnEvaluation.period_end}
                onChange={(e) => setNewRnEvaluation(prev => ({ ...prev, period_end: e.target.value }))}
              />
            </div>
          </div>

          {/* Grading Scale Legend */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Evaluation Scale</p>
            <div className="flex flex-wrap gap-2">
              {rnGradingScale.map((grade) => (
                <span key={grade.value} className={`px-2 py-1 rounded text-xs ${getGradeColor(grade.value)}`}>
                  {grade.value} = {grade.label}
                </span>
              ))}
            </div>
          </div>

          {/* RN Duties Evaluation */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">RESPONSIBILITIES/DUTIES - Agency Management</h3>
            
            {rnDuties.map((duty) => (
              <div key={duty.id} className="p-3 border rounded-lg hover:bg-slate-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">
                      <span className="text-blue-600 mr-2">#{duty.number}</span>
                      {duty.description}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((grade) => (
                      <button
                        key={grade}
                        type="button"
                        onClick={() => setNewRnEvaluation(prev => ({ ...prev, [duty.id]: grade }))}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                          newRnEvaluation[duty.id] === grade
                            ? getGradeColor(grade) + " ring-2 ring-offset-1"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {grade}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Goals */}
          <div className="space-y-2">
            <Label>Goal Setting</Label>
            <Textarea
              value={newRnEvaluation.goals}
              onChange={(e) => setNewRnEvaluation(prev => ({ ...prev, goals: e.target.value }))}
              placeholder="Set goals and objectives for the next evaluation period..."
              rows={3}
            />
          </div>

          {/* Additional Comments */}
          <div className="space-y-2">
            <Label>Additional Comments</Label>
            <Textarea
              value={newRnEvaluation.additional_comments}
              onChange={(e) => setNewRnEvaluation(prev => ({ ...prev, additional_comments: e.target.value }))}
              placeholder="Additional observations, commendations, or areas for development..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? "Saving..." : "Save RN Evaluation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
